import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { ringGroupsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

const RingGroupsPage = () => {
  const [ringGroups, setRingGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, group: null });

  useEffect(() => {
    fetchRingGroups();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      setFilteredGroups(
        ringGroups.filter(
          (group) =>
            group.extension?.toString().includes(query) ||
            group.name?.toLowerCase().includes(query) ||
            group.description?.toLowerCase().includes(query)
        )
      );
    } else {
      setFilteredGroups(ringGroups);
    }
  }, [searchQuery, ringGroups]);

  const fetchRingGroups = async () => {
    try {
      const response = await ringGroupsAPI.getAll();
      const data = response.data?.data || response.data || [];
      setRingGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch ring groups:', error);
      toast.error('Failed to load ring groups');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.group) return;
    
    try {
      await ringGroupsAPI.delete(deleteModal.group.id || deleteModal.group.extension);
      toast.success('Ring group deleted successfully');
      setDeleteModal({ open: false, group: null });
      fetchRingGroups();
    } catch (error) {
      console.error('Failed to delete ring group:', error);
      toast.error('Failed to delete ring group');
    }
  };

  const getStrategyLabel = (strategy) => {
    const strategies = {
      ringall: 'Ring All',
      hunt: 'Hunt',
      memoryhunt: 'Memory Hunt',
      firstnotonphone: 'First Available',
      random: 'Random',
    };
    return strategies[strategy] || strategy || 'Ring All';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Ring Groups</h1>
          <p className="text-slate-400 mt-1">
            Configure how calls are distributed to groups of extensions
          </p>
        </div>
        <Link to="/ring-groups/new" className="btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          Create Ring Group
        </Link>
      </div>

      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input
          type="text"
          placeholder="Search ring groups..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-10 max-w-md"
        />
      </div>

      {filteredGroups.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGroups.map((group) => (
            <div
              key={group.id || group.extension}
              className="card hover:border-primary-500/30 transition-all duration-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-accent-500/20">
                    <UserGroupIcon className="w-6 h-6 text-accent-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{group.name || `Ring Group ${group.extension}`}</h3>
                    <p className="text-sm text-primary-400 font-mono">{group.extension}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Link
                    to={`/ring-groups/${group.id || group.extension}/edit`}
                    className="p-2 text-slate-400 hover:text-primary-400 hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => setDeleteModal({ open: true, group })}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-800">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Strategy</p>
                    <p className="text-slate-300">{getStrategyLabel(group.strategy)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Members</p>
                    <p className="text-slate-300">{group.members?.length || group.extension_list?.split('-').length || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Ring Time</p>
                    <p className="text-slate-300">{group.ring_time || group.ringtime || 20}s</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Status</p>
                    <span className="badge badge-success">Active</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
            <UserGroupIcon className="w-8 h-8 text-slate-600" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No ring groups found</h3>
          <p className="text-slate-400 mb-4">
            {searchQuery ? 'Try adjusting your search terms' : 'Create your first ring group to distribute calls'}
          </p>
          {!searchQuery && (
            <Link to="/ring-groups/new" className="btn-primary inline-flex">
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Ring Group
            </Link>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, group: null })}
        onConfirm={handleDelete}
        title="Delete Ring Group"
        message={`Are you sure you want to delete "${deleteModal.group?.name || deleteModal.group?.extension}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default RingGroupsPage;
